import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { companyApi, companyQueryKeys } from "service/crm/api/company";
import { NonAssociatedContactCompanyParams } from "types/crm/company";

interface UseQueryOptions {
  enabled?: boolean;
}

export const useContactCompanies = (
  contactUuid: string,
  options: UseQueryOptions = {},
) => {
  return useQuery({
    queryKey: companyQueryKeys.byContact(contactUuid),
    queryFn: () => companyApi.getCompaniesByContact(contactUuid),
    enabled: (options.enabled ?? true) && !!contactUuid,
  });
};

/**
 * Org companies not yet associated with the contact — for the add-search UI.
 * Search is server-side; pages are appended as the list is scrolled.
 */
export const useNonAssociatedCompanies = (
  contactUuid: string,
  params: Omit<NonAssociatedContactCompanyParams, "page"> = {},
  options: UseQueryOptions = {},
) => {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: companyQueryKeys.nonAssociated(contactUuid, params),
    queryFn: ({ pageParam }) =>
      companyApi.getNonAssociatedCompanies(contactUuid, {
        ...params,
        page: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.last ? undefined : lastPage.page + 1,
    enabled: (options.enabled ?? true) && !!contactUuid,
  });

  return {
    companies: data?.pages.flatMap((p) => p.content) ?? [],
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements: data?.pages[0]?.totalElements ?? 0,
  };
};
